import {
  onLCP,
  onINP,
  onCLS,
  onTTFB,
  onFCP,
  type Metric,
  type MetricRating,
  type LCPAttribution,
  type CLSAttribution,
  type INPAttribution,
} from "web-vitals";

/* ─── Types ─────────────────────────────────────────────────── */

type AttributionSummary = {
  element?: string;
  resource?: string;
  lcpRenderTime?: number;
  lcpLoadTime?: number;
  lcpSize?: number;
  clsSourceElements?: string[];
  inpEventType?: string;
  inpEventTarget?: string;
  inpDuration?: number;
};

type VitalPayload = {
  name: string;
  value: number;
  rating: MetricRating;
  id: string;
  delta?: number;
  navigationType?: string;
  attribution?: AttributionSummary;
};

/* ─── Thresholds ─────────────────────────────────────────────── */

const THRESHOLDS: Record<string, { good: number; poor: number }> = {
  LCP: { good: 2500, poor: 4000 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  TTFB: { good: 800, poor: 1800 },
  FCP: { good: 1800, poor: 3000 },
};

/* ─── Storage ────────────────────────────────────────────────── */

const recentVitals: VitalPayload[] = [];
const MAX_STORED = 20;

function storeVital(payload: VitalPayload): void {
  recentVitals.push(payload);
  if (recentVitals.length > MAX_STORED) recentVitals.shift();
}

/* ─── Attribution extraction ─────────────────────────────────── */

function extractAttribution(metric: Metric & { attribution?: unknown }): AttributionSummary | undefined {
  if (!metric.attribution) return undefined;

  const att = metric.attribution as Record<string, unknown>;
  const result: AttributionSummary = {};

  // LCP: element + resource URL + timing breakdown
  if ((att as LCPAttribution).largestShiftTarget) {
    const lcpAtt = att as unknown as LCPAttribution;
    if (lcpAtt.element) result.element = lcpAtt.element;
    if (lcpAtt.url) result.resource = lcpAtt.url;
    if (lcpAtt.largestShiftTime != null) result.lcpRenderTime = Math.round(lcpAtt.largestShiftTime);
    if (lcpAtt.largestShiftValue != null) result.lcpSize = Math.round(lcpAtt.largestShiftValue);
  }

  // CLS: list of contributing elements
  if ((att as CLSAttribution).largestShiftTarget) {
    const clsAtt = att as unknown as CLSAttribution;
    if (clsAtt.largestShiftTarget) {
      result.clsSourceElements = [clsAtt.largestShiftTarget as string];
    }
  }

  // INP: event type + target + interaction duration
  if ((att as INPAttribution).eventType) {
    const inpAtt = att as unknown as INPAttribution;
    if (inpAtt.eventType) result.inpEventType = inpAtt.eventType;
    if (inpAtt.eventTarget) result.inpEventTarget = inpAtt.eventTarget;
    if (inpAtt.eventTime != null) result.inpDuration = Math.round(inpAtt.eventTime);
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

/* ─── Formatting ─────────────────────────────────────────────── */

function formatVital(payload: VitalPayload): string {
  const t = THRESHOLDS[payload.name];
  const status = payload.rating === "good" ? "good" : payload.rating === "needs-improvement" ? "needs-improvement" : "poor";

  const unit = payload.name === "CLS" ? "" : "ms";
  const rounded = payload.name === "CLS" ? Math.round(payload.value * 1000) / 1000 : Math.round(payload.value);

  let msg = `[CWV] ${payload.name}: ${rounded}${unit} (${status})`;
  if (t) {
    msg += ` — threshold: good <${t.good}${unit}, poor >${t.poor}${unit}`;
  }

  if (payload.attribution) {
    const a = payload.attribution;
    if (a.element) msg += ` | el: ${a.element}`;
    if (a.resource) msg += ` | resource: ${a.resource}`;
    if (a.inpEventType) msg += ` | event: ${a.inpEventType} on ${a.inpEventTarget || "?"}`;
    if (a.clsSourceElements) msg += ` | sources: ${a.clsSourceElements.join(", ")}`;
  }

  return msg;
}

function reportToConsole(payload: VitalPayload): void {
  const formatted = formatVital(payload);
  if (payload.rating === "poor") {
    console.warn(formatted, payload);
  } else if (payload.rating === "needs-improvement") {
    console.warn(formatted, payload);
  } else {
    console.info(formatted, payload);
  }
}

/* ─── Analytics beacon (future-ready) ─────────────────────────── */

function sendToAnalyticsIfConfigured(payload: VitalPayload): void {
  if (typeof navigator === "undefined" || !navigator.sendBeacon) return;

  const endpoint = (import.meta as unknown as Record<string, string>).env?.VITE_ANALYTICS_ENDPOINT;
  if (!endpoint) return;

  try {
    navigator.sendBeacon(endpoint, JSON.stringify({
      ...payload,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
    }));
  } catch {
    // Beacon silently fails — no console noise
  }
}

/* ─── LCP element observer (field diagnostics) ─────────────────── */

function initLCPElementObserver(): void {
  if (!("PerformanceObserver" in window)) return;

  try {
    const lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (!last) return;

      const el = (last as PerformanceEntry & { element?: Element }).element;
      if (!el) {
        console.info("[CWV] LCP element: (no element reference — may be a text node or background image)");
        return;
      }

      const classList = el.className ? " ." + String(el.className).split(" ").slice(0, 3).join(" .") : "";
      const idPart = el.id ? " #" + el.id : "";
      console.info(`[CWV] LCP element: ${el.tagName}${idPart}${classList}`, el);
    });
    lcpObs.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    // PerformanceObserver not supported or LCP type unavailable
  }
}

/* ─── Memory pressure monitor (INP insight) ───────────────────── */

function initMemoryMonitor(): void {
  if (!("performance" in window) || !("memory" in (performance as unknown as Record<string, unknown>))) return;

  const mem = (performance as unknown as Record<string, unknown>).memory as Record<string, number> | undefined;
  if (!mem || typeof mem.usedJSHeapSize !== "number") return;

  // Log heap size periodically for debugging INP spikes caused by GC pauses
  setTimeout(() => {
    const usedMB = Math.round(mem.usedJSHeapSize / 1024 / 1024);
    const limitMB = Math.round(mem.jsHeapSizeLimit / 1024 / 1024);
    if (usedMB > limitMB * 0.75) {
      console.warn(`[CWV] Memory pressure: ${usedMB}MB / ${limitMB}MB — may contribute to INP spikes`);
    }
  }, 5000);
}

/* ─── Handler factory ────────────────────────────────────────── */

function createHandler(name: string) {
  return (metric: Metric) => {
    const attribution = extractAttribution(metric as Metric & { attribution?: unknown });
    const payload: VitalPayload = {
      name,
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      delta: metric.delta,
      navigationType: metric.navigationType,
      attribution,
    };

    storeVital(payload);
    reportToConsole(payload);
    sendToAnalyticsIfConfigured(payload);
  };
}

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Initialize Core Web Vitals field measurement.
 * Call once after app mount. Metrics report when stabilized in real sessions.
 */
export function initWebVitals(): void {
  // LCP: report the largest element. Pass {reportAllChanges: false} to only report final value.
  onLCP(createHandler("LCP"), { reportAllChanges: false });

  // CLS: report layout shift score. Pass {reportAllChanges: false} for final value only.
  onCLS(createHandler("CLS"), { reportAllChanges: false });

  // INP: report interaction to next paint. Pass {reportAllChanges: false} for final value.
  onINP(createHandler("INP"), { reportAllChanges: false });

  // TTFB: report time to first byte (no options for reportAllChanges).
  onTTFB(createHandler("TTFB"));

  // FCP: report first contentful paint (no options for reportAllChanges).
  onFCP(createHandler("FCP"));

  // Expose summary helper for quick field auditing in console
  (window as unknown as Record<string, unknown>).__WEB_VITALS__ = {
    summary: () => {
      const latest: Record<string, VitalPayload> = {};
      [...recentVitals].reverse().forEach((v) => {
        if (!latest[v.name]) latest[v.name] = v;
      });
      console.table(latest);
      return latest;
    },
    all: () => [...recentVitals],
    clear: () => {
      recentVitals.length = 0;
      console.info("[CWV] Cleared stored vital history.");
    },
    thresholds: THRESHOLDS,
  };

  initLCPElementObserver();
  initMemoryMonitor();

  console.info("[CWV] Core Web Vitals field instrumentation active (v2 — with attribution).");
  console.info("[CWV] Type __WEB_VITALS__.summary() in console to see latest metrics.");
  console.info("[CWV] Type __WEB_VITALS__.thresholds to see pass/fail values.");
}